import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, Alert, Box, Button, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme, Typography } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { Icon } from '@iconify/react';
import StyledSearchInput from '@ui/components/StyledSearchInput';
import { useForm } from 'react-hook-form';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
const initialCollaborators = [
    {
        id: '1',
        person: { name: 'Amelia Harper', designation: 'CEO' },
        company: 'VDK Group',
        status: 'assigned'
    },
    {
        id: '2',
        person: { name: 'Lucas Vermeulen', designation: 'CTO' },
        company: 'VDK Group',
        status: 'available'
    },
    {
        id: '3',
        person: { name: 'Charlotte Visser', designation: 'CFO' },
        company: 'VDK Events',
        status: 'justAssigned'
    }
];
const initialManualEntry = {
    name: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    referral: ''
};
const AssignTicketsDialog = ({ open, onClose, mode, ticketName, remainingTickets, onConfirm }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [collaborators, setCollaborators] = useState(initialCollaborators.map((collaborator) => ({ ...collaborator })));
    const [selectedMode, setSelectedMode] = useState(mode);
    const [searchValue, setSearchValue] = useState('');
    const [selection, setSelection] = useState([]);
    const { control, watch, reset } = useForm({ defaultValues: initialManualEntry });
    useEffect(() => {
        setSelectedMode(mode);
        setSelection([]);
        reset(initialManualEntry);
        setCollaborators(initialCollaborators.map((collaborator) => ({ ...collaborator })));
        setSearchValue('');
    }, [mode, open, reset]);
    const manualEntry = watch();
    const filteredCollaborators = useMemo(() => collaborators.filter((collaborator) => [collaborator.person.name, collaborator.company, collaborator.person.designation]
        .join(' ')
        .toLowerCase()
        .includes(searchValue.toLowerCase())), [collaborators, searchValue]);
    const columns = [
        {
            field: 'person',
            headerName: t('sponsorships.tickets.assign.columns.person'),
            flex: 1,
            minWidth: 160,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, children: params.value.name }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', noWrap: true, children: params.value.designation })] }))
        },
        {
            field: 'company',
            headerName: t('sponsorships.tickets.assign.columns.company'),
            flex: 1,
            minWidth: 160
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                const status = params.row.status ?? 'available';
                const isAlreadyAssigned = status === 'assigned';
                const isJustAssigned = status === 'justAssigned';
                const isAvailable = status === 'available';
                const variant = isJustAssigned
                    ? 'contained'
                    : isAlreadyAssigned
                        ? 'contained'
                        : 'soft';
                const label = isJustAssigned
                    ? t('sponsorships.tickets.assigned')
                    : t('sponsorships.tickets.assignLabel');
                return (_jsx(Button, { variant: variant, color: 'secondary', size: 'medium', disabled: isAlreadyAssigned, startIcon: isJustAssigned || isAvailable ? _jsx(Icon, { icon: 'eva:checkmark-fill' }) : undefined, sx: {
                        textTransform: 'none',
                        bgcolor: isJustAssigned ? '#4D850C' : undefined,
                        color: isAvailable ? theme.palette.success.dark : undefined
                    }, onClick: () => {
                        if (!isAlreadyAssigned) {
                            setSelection([params.row.id]);
                        }
                    }, children: label }));
            }
        }
    ];
    const handleSave = () => {
        if (selectedMode === 'database') {
            const selectedCollaborator = collaborators.find((collaborator) => collaborator.id === selection[0]);
            if (selectedCollaborator) {
                setCollaborators((prev) => prev.map((collaborator) => {
                    if (collaborator.id === selectedCollaborator.id) {
                        return { ...collaborator, status: 'justAssigned' };
                    }
                    if (collaborator.status === 'justAssigned') {
                        return { ...collaborator, status: 'assigned' };
                    }
                    return collaborator;
                }));
                onConfirm({ mode: selectedMode, collaborator: selectedCollaborator });
                onClose();
            }
        }
        else {
            const manualPayload = {
                ...manualEntry,
                name: manualEntry.name ||
                    [manualEntry.firstName, manualEntry.middleName, manualEntry.lastName]
                        .map((part) => part.trim())
                        .filter(Boolean)
                        .join(' ')
            };
            onConfirm({ mode: selectedMode, manual: manualPayload });
            onClose();
        }
    };
    const isSaveDisabled = selectedMode === 'database'
        ? selection.length === 0
        : !manualEntry.firstName.trim() || !manualEntry.lastName.trim() || !manualEntry.email.trim();
    const primaryActionLabel = selectedMode === 'database' ? t('actions.saveChanges') : t('sponsorships.tickets.assignNow');
    const primaryActionIcon = selectedMode === 'database' ? 'eva:checkmark-fill' : 'eva:checkmark-fill';
    const content = selectedMode === 'database' ? (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsx(StyledSearchInput, { fullWidth: true, value: searchValue, onChange: (event) => setSearchValue(event.currentTarget.value), handleResetCallback: () => setSearchValue('') }), _jsx(PrimaryGrid, { rows: filteredCollaborators, columns: columns, rowSelectionModel: selection, onRowSelectionModelChange: (model) => setSelection(model.length > 1 ? [model[model.length - 1]] : model), showFilters: false, showHeaderControls: false, haveSearch: false, hideFooterSelectedRowCount: true, disableRowSelectionOnClick: true, paginationModel: { page: 0, pageSize: 5 }, pageSizeOptions: [5], maxHeight: '200px' })] })) : (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            maxHeight: 'calc(100dvh - 400px)',
            overflowY: 'auto',
            pr: 0.5
        }, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.tickets.assign.firstName'), control: control, name: 'firstName' }), _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.tickets.assign.middleName'), control: control, name: 'middleName' }), _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.tickets.assign.lastName'), control: control, name: 'lastName' }), _jsx(PhoneInputMasking, { bg: 'grey8', label: t('sponsorships.tickets.assign.phone'), control: control, name: 'phone' }), _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.tickets.assign.email'), control: control, name: 'email', type: 'email' }), _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.tickets.assign.company'), control: control, name: 'company' }), _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.tickets.assign.referral'), control: control, name: 'referral' })] }));
    const body = (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', px: 3, py: 2, gap: '20px' }, children: [_jsx(Alert, { variant: 'outlined', severity: 'info', sx: { bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: '12px' }, children: t('sponsorships.tickets.assign.ticketsLeft', { count: remainingTickets }) }), _jsxs(ToggleButtonGroup, { size: 'large', fullWidth: true, exclusive: true, value: selectedMode, onChange: (_, value) => value && setSelectedMode(value), sx: {
                    alignSelf: 'flex-start',
                    borderRadius: 2,
                    minHeight: '48px',
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    '& .MuiToggleButtonGroup-grouped': {
                        border: 'none',
                        borderRadius: 0,
                        '&:not(:last-of-type)': {
                            borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`
                        },
                        '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.darker,
                            color: theme.palette.common.white,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.darker
                            }
                        },
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08)
                        }
                    }
                }, children: [_jsx(ToggleButton, { value: 'database', sx: {
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            fontWeight: 500
                        }, children: t('sponsorships.tickets.assign.database') }), _jsx(ToggleButton, { value: 'manual', sx: {
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            fontWeight: 500
                        }, children: t('sponsorships.tickets.assign.manual') })] }), content] }));
    const actions = (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, disabled: isSaveDisabled, onClick: handleSave, endIcon: _jsx(Icon, { icon: primaryActionIcon, width: 20, height: 20 }), children: primaryActionLabel })] }));
    if (isMobile) {
        return (_jsx(BDrawer, { open: open, onClose: onClose, title: t('sponsorships.tickets.assign.title', { ticket: ticketName }), dialogActions: actions, children: body }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: onClose, titleWithIcon: t('sponsorships.tickets.assign.assignTickets'), titleIcon: '/assets/images/starIcon.png', actions: actions, maxWidth: selectedMode === 'database' ? 'md' : 'sm', children: body }));
};
export default AssignTicketsDialog;
